import {
  Address,
  fromNano,
  JettonMaster,
  TonClient,
  TonClient4,
  TupleReader,
} from "@ton/ton";
import { TonConnectUI } from "@tonconnect/ui-react";
import { action, flow, flowResult, makeObservable, observable } from "mobx";
import { Ticker } from "../../entities/Ticker/TickerTypes";
import { Sender } from "../../lib/contracts/Sender2";
import { JettonWallet } from "../../lib/wrappers/JettonWallet";
import { tokenList } from "../../utils/AppConstants";
import raceGuard from "../../utils/raceGuard";
import BusyStore from "../common/BusyStore";

export const getClient = async () => {
  // const endpoint = await getHttpEndpoint();
  // console.log("nik endpoint", endpoints);
  return new TonClient({ endpoint: "https://toncenter.com/api/v2/jsonRPC" });
};

export const getClient4 = async () => {
  return new TonClient4({ endpoint: "https://mainnet-v4.tonhubapi.com" });
};

class TonClientStore extends BusyStore {
  tonClient4: TonClient4 | undefined;

  tonClient: TonClient | undefined;

  sender: Sender | undefined;

  tonBalance: number | null = null;
  raffBalance: number | null = null;
  usdtBalance: number | null = null;
  isBalanceFetchLoading = false;

  isWalletV5: boolean | null = false;

  constructor() {
    super();
    makeObservable<
      TonClientStore,
      | "loadClient4Flow"
      | "loadClientFlow"
      | "loadSenderFlow"
      | "loadTonBalanceFlow"
      | "loadJettonBalanceFlow"
      | "getWalletVersionFlow"
    >(this, {
      tonClient4: observable,
      tonClient: observable,
      sender: observable,
      isWalletV5: observable,

      tonBalance: observable,
      raffBalance: observable,
      usdtBalance: observable,
      isBalanceFetchLoading: observable,

      setTonBalance: action,

      loadTonBalanceFlow: flow,
      loadJettonBalanceFlow: flow,

      loadSenderFlow: flow,
      loadClient4Flow: flow,
      loadClientFlow: flow,
      getWalletVersionFlow: flow,
    });
  }

  setTonBalance(balance: number) {
    this.tonBalance = balance;
  }

  loadTonBalance(userAddress: string): Promise<void> {
    return flowResult(this.loadTonBalanceFlow(userAddress));
  }

  loadRaffBalance(userAddress: string) {
    return flowResult(this.loadJettonBalanceFlow(userAddress, Ticker.RAFF));
  }

  loadUsdtBalance(userAddress: string) {
    return flowResult(this.loadJettonBalanceFlow(userAddress, Ticker.USDT));
  }

  private *loadJettonBalanceFlow(userAddress: string, ticker: Ticker) {
    if (userAddress === "") return;
    this.isBalanceFetchLoading = true;
    const jettonMasterAddressString = tokenList.filter(
      (item) => item.value === ticker
    );
    const jettonMasterAddress = Address.parse(
      jettonMasterAddressString[0].address
    );
    try {
      if (!this.tonClient4) return;

      const jettonMaster = this.tonClient4.open(
        JettonMaster.create(jettonMasterAddress)
      );
      const jettonWallet = this.tonClient4.open(
        JettonWallet.createFromAddress(
          yield jettonMaster.getWalletAddress(Address.parse(userAddress))
        )
      );
      const value: bigint = yield jettonWallet.getJettonBalance();

      if (ticker === Ticker.USDT) {
        this.usdtBalance = parseFloat(fromNano(value)) * Math.pow(10, 3);
      } else if (ticker === Ticker.RAFF) {
        this.raffBalance = Number(fromNano(value));
      }
    } catch (_: unknown) {
      if (!this.tonClient) return;

      const jettonMaster = this.tonClient.open(
        JettonMaster.create(jettonMasterAddress)
      );
      const jettonWallet = this.tonClient.open(
        JettonWallet.createFromAddress(
          yield jettonMaster.getWalletAddress(Address.parse(userAddress))
        )
      );

      const value: bigint = yield jettonWallet.getJettonBalance();

      if (ticker === Ticker.USDT) {
        this.usdtBalance = parseFloat(fromNano(value)) * Math.pow(10, 3);
      } else if (ticker === Ticker.RAFF) {
        this.raffBalance = Number(fromNano(value));
      }
    } finally {
      this.isBalanceFetchLoading = false;
    }
  }

  private *loadTonBalanceFlow(userAddress: string) {
    if (userAddress === "") return;
    this.isBalanceFetchLoading = true;
    try {
      if (!this.tonClient4) return;

      const seqno: number = yield this.tonClient4
        .getLastBlock()
        .then((result) => result.last.seqno);

      const result: string = yield this.tonClient4
        .getAccountLite(seqno, Address.parse(userAddress))
        .then((result) => result.account.balance.coins);

      this.tonBalance = Number(fromNano(result));
    } catch (_: unknown) {
      if (!this.tonClient) return;

      const parsedUserAddress = Address.parse(userAddress);
      const balanceNanoton: bigint = yield this.tonClient.getBalance(
        parsedUserAddress
      );

      this.tonBalance = Number(fromNano(balanceNanoton));
    } finally {
      this.isBalanceFetchLoading = false;
    }
  }

  readonly loadClient = raceGuard(
    async () => await flowResult(this.loadClientFlow())
  );

  private *loadClientFlow() {
    this.onLoadingStart();
    try {
      const client: TonClient = yield getClient();

      this.tonClient = client;

      return this.tonClient;
    } finally {
      this.onLoadingFinished();
    }
  }

  readonly loadWalletVersion = (userAddress: string) =>
    raceGuard(
      async () => await flowResult(this.getWalletVersionFlow(userAddress))
    )();

  readonly loadClient4 = raceGuard(
    async () => await flowResult(this.loadClient4Flow())
  );

  readonly loadSender = (tonConnectUI: TonConnectUI) =>
    raceGuard(
      async () => await flowResult(this.loadSenderFlow(tonConnectUI))
    )();

  private *loadClient4Flow() {
    this.onLoadingStart();
    try {
      // console.log("nik this.tonClient4 before", this.tonClient4);

      this.tonClient4 = yield getClient4();
      // console.log("nik this.tonClient4", this.tonClient4);
      return this.tonClient4;
    } catch (_) {
      return;
    } finally {
      this.onLoadingFinished();
    }
  }

  private *loadSenderFlow(tonConnectUI: TonConnectUI) {
    this.onLoadingStart();
    try {
      if (tonConnectUI && tonConnectUI.connector) {
        const sender = new Sender(tonConnectUI);
        this.sender = yield Promise.resolve(sender);
        return this.sender;
      }
      return null;
    } finally {
      this.onLoadingFinished();
    }
  }

  private *getWalletVersionFlow(userAddress: string) {
    if (userAddress === "") {
      this.isWalletV5 = false;
      return;
    }

    try {
      if (!this.tonClient) {
        yield this.loadClient();
      }

      if (this.tonClient) {
        const _: {
          gas_used: number;
          stack: TupleReader;
        } = yield this.tonClient.runMethod(
          Address.parse(userAddress),
          "is_signature_allowed"
        );
        this.isWalletV5 = true;
      }
    } catch (_) {
      this.isWalletV5 = false;
    }
  }
}

export default TonClientStore;
